import { create } from "zustand";
import type {
  FileNode,
  Project,
  EditorTab,
  ChatSession,
  ChatMessage,
} from "../+types/project";
import {
  getProject,
  getTemplatePreview,
  compileSolidity,
  downloadProject,
  handleFiles,
  type HandleFilesResponse,
} from "../services/chat.services";
import {
  getUserTokenFromJWT,
  downloadBase64File,
  addRecentProject,
} from "../lib/utils";
import { getLanguageFromExtension } from "../+types/project";
import JSZip from "jszip";
import useCompilationStore from "./compilationStore";
import useUnitTestStore from "./unitTestStore";
import useChatMessagesStore from "./chatMessagesStore";
import {
  extractChatIdFromResponse,
  normalizeProjectId,
  projectIdsMatch,
} from "~/lib/chatMessageSync";
import useNotificationStore from "./notificationStore";
import { useSubscriptionStore } from "./subscriptionStore";
import useCodeGeneratorStore from "./codeGeneratorStore";
import useDownloadStore from "./downloadStore";

/**
 * Module-level counter used to implement a "last write wins" abort pattern
 * for concurrent loadProject calls. Each call captures the callId at start;
 * any state mutation is skipped if a newer call has superseded the current one.
 */
let _loadCallId = 0;

// Helper to extract file contents from tree
const extractContents = (nodes: FileNode[]): Record<string, string> => {
  let contents: Record<string, string> = {};
  for (const node of nodes) {
    if (node.type === "file" && node.content) {
      contents[node.path] = node.content;
    }
    if (node.children) {
      contents = { ...contents, ...extractContents(node.children) };
    }
  }
  return contents;
};

const checkIfHasContract = (nodes: FileNode[]): boolean => {
  for (const node of nodes) {
    if (node.type === "file" && node.path.toLowerCase().endsWith(".sol"))
      return true;
    if (node.children && checkIfHasContract(node.children)) return true;
  }
  return false;
};

const findNodeById = (nodes: FileNode[], id: string): FileNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const normalizeNodePath = (path: string): string =>
  (path || "").replace(/^\/+/, "");

const isHandleFilesSuccess = (response?: HandleFilesResponse): boolean =>
  Boolean(response?.results?.length) &&
  response!.results.every((result) => result.success);

const shouldSkipHandleFiles = (readOnly: boolean): boolean => readOnly;

const mapPathPrefix = (
  path: string | null,
  oldPrefix: string,
  newPrefix: string,
): string | null => {
  if (!path) return path;
  const normalizedPath = normalizeNodePath(path);
  if (normalizedPath === oldPrefix) return newPrefix;
  if (normalizedPath.startsWith(`${oldPrefix}/`)) {
    return `${newPrefix}${normalizedPath.slice(oldPrefix.length)}`;
  }
  return path;
};

const remapPathKeys = (
  source: Record<string, string>,
  oldPrefix: string,
  newPrefix: string,
): Record<string, string> => {
  const remapped: Record<string, string> = {};
  Object.entries(source).forEach(([key, value]) => {
    const mappedKey = mapPathPrefix(key, oldPrefix, newPrefix) || key;
    remapped[mappedKey] = value;
  });
  return remapped;
};

const findNodeByPath = (nodes: FileNode[], path: string): FileNode | null => {
  const normalizedPath = normalizeNodePath(path);
  for (const node of nodes) {
    if (normalizeNodePath(node.path) === normalizedPath) return node;
    if (node.children) {
      const found = findNodeByPath(node.children, normalizedPath);
      if (found) return found;
    }
  }
  return null;
};

const toAbsolutePath = (path: string): string => {
  const normalized = normalizeNodePath(path);
  return normalized ? `/${normalized}` : "/";
};

const cloneNodes = (nodes: FileNode[]): FileNode[] =>
  nodes.map((node) => ({
    ...node,
    children: node.children ? cloneNodes(node.children) : undefined,
  }));

const dedupeNodesByPath = (nodes: FileNode[]): FileNode[] => {
  const uniqueNodes = new Map<string, FileNode>();
  nodes.forEach((node) => {
    const pathKey = normalizeNodePath(node.path || node.name || "");
    if (!pathKey) return;
    // Last value wins in case backend sends duplicates.
    uniqueNodes.set(pathKey, node);
  });
  return Array.from(uniqueNodes.values());
};

/** Canonical key for folder expand/collapse UI state (decoupled from tree data). */
export const folderExpandKey = (node: Pick<FileNode, "path" | "id">): string =>
  normalizeNodePath(node.path || node.id);

export const isFolderExpandedInSet = (
  expandedFolderKeys: Set<string>,
  node: Pick<FileNode, "path" | "id">,
): boolean => {
  const pathKey = normalizeNodePath(node.path);
  const idKey = normalizeNodePath(node.id);
  return expandedFolderKeys.has(pathKey) || expandedFolderKeys.has(idKey);
};

const remapExpandedFolderKeys = (
  keys: Set<string>,
  oldPrefix: string,
  newPrefix: string,
): Set<string> => {
  const next = new Set<string>();
  keys.forEach((key) => {
    const mapped = mapPathPrefix(key, oldPrefix, newPrefix);
    if (mapped) next.add(normalizeNodePath(mapped));
  });
  return next;
};

const pruneExpandedFolderKeys = (
  keys: Set<string>,
  deletedPrefix: string,
): Set<string> => {
  const normalized = normalizeNodePath(deletedPrefix);
  const next = new Set<string>();
  keys.forEach((key) => {
    if (key === normalized || key.startsWith(`${normalized}/`)) return;
    next.add(key);
  });
  return next;
};

/** One-time import when a loaded tree still carries legacy `isOpen` flags. */
const collectExpandedKeysFromTree = (nodes: FileNode[]): Set<string> => {
  const keys = new Set<string>();
  const walk = (items: FileNode[]) => {
    for (const node of items) {
      if (node.type === "folder" && node.isOpen) {
        keys.add(folderExpandKey(node));
      }
      if (node.children?.length) walk(node.children);
    }
  };
  walk(nodes);
  return keys;
};

export const resolveActiveContractPath = (
  activeFile: string | null,
  activeTabId: string | null,
): string | null => {
  for (const candidate of [activeFile, activeTabId]) {
    if (candidate?.toLowerCase().endsWith(".sol")) {
      return toAbsolutePath(candidate);
    }
  }
  return null;
};

const upsertSingleNode = (
  tree: FileNode[],
  incomingNode: FileNode,
): FileNode[] => {
  const incomingPath = toAbsolutePath(incomingNode.path || incomingNode.name);
  const pathSegments = normalizeNodePath(incomingPath)
    .split("/")
    .filter(Boolean);
  if (pathSegments.length === 0) return tree;

  let currentLevel = tree;
  let accumulatedPath = "";

  const parentSegments = pathSegments.slice(0, -1);
  for (const segment of parentSegments) {
    accumulatedPath = `${accumulatedPath}/${segment}`;
    const normalizedAccumulatedPath = normalizeNodePath(accumulatedPath);
    let folder = currentLevel.find(
      (node) =>
        node.type === "folder" &&
        normalizeNodePath(node.path) === normalizedAccumulatedPath,
    );

    if (!folder) {
      folder = {
        id: normalizedAccumulatedPath,
        name: segment,
        type: "folder",
        path: toAbsolutePath(accumulatedPath),
        children: [],
      };
      currentLevel.push(folder);
    }

    if (!folder.children) folder.children = [];
    currentLevel = folder.children;
  }

  const existingNode = currentLevel.find(
    (node) => normalizeNodePath(node.path) === normalizeNodePath(incomingPath),
  );

  const normalizedIncomingNode: FileNode = {
    ...incomingNode,
    id: incomingNode.id || normalizeNodePath(incomingPath),
    name: incomingNode.name || pathSegments[pathSegments.length - 1],
    path: incomingPath,
  };

  if (existingNode) {
    if (
      existingNode.type === "folder" &&
      normalizedIncomingNode.type === "folder"
    ) {
      const incomingChildren = normalizedIncomingNode.children
        ? [...normalizedIncomingNode.children]
        : [];
      Object.assign(existingNode, {
        ...existingNode,
        ...normalizedIncomingNode,
        path: incomingPath,
        // Keep existing folder children; merge incoming children recursively below.
        children: existingNode.children || [],
      });
      incomingChildren.forEach((child) => {
        upsertSingleNode(tree, child);
      });
    } else {
      Object.assign(existingNode, {
        ...existingNode,
        ...normalizedIncomingNode,
        path: incomingPath,
      });
    }
  } else {
    currentLevel.push(normalizedIncomingNode);
  }

  if (
    !existingNode &&
    incomingNode.type === "folder" &&
    incomingNode.children?.length
  ) {
    incomingNode.children.forEach((child) => {
      upsertSingleNode(tree, child);
    });
  }

  return tree;
};

const buildFileContentsMap = (nodes: FileNode[]): Record<string, string> => {
  const contents: Record<string, string> = {};
  const walk = (items: FileNode[]) => {
    items.forEach((node) => {
      if (node.type === "file" && typeof node.content === "string") {
        contents[toAbsolutePath(node.path)] = node.content;
      }
      if (node.children?.length) {
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return contents;
};

interface TestsSummary {
  passed: number;
  failed: number;
  running: boolean;
}

interface ProjectStore {
  // --- New / Updated Fields ---
  projectId: string | null;
  userId: string | null;
  fileTree: FileNode[] | null;
  /** Normalized folder paths/ids that are expanded in the explorer (UI-only). */
  expandedFolderKeys: Set<string>;
  openFiles: string[]; // Paths of open files
  activeFile: string | null; // Path of active file
  fileContents: Record<string, string>; // Map path -> content
  chatMessages: {
    role: "user" | "assistant" | "system";
    content: string;
    timestamp?: string;
  }[];
  terminalOutput: string[];
  testsSummary: TestsSummary | null;
  loading: boolean;
  error: string | null;
  readOnly: boolean;
  isLoginModalOpen: boolean;

  // --- Preservation of existing UI state (needed for components) ---
  isSidebarCollapsed: boolean;
  isChatPanelOpen: boolean;
  isBottomPanelOpen: boolean;
  bottomPanelHeight: number;
  terminalActiveTab: "compile" | "test";

  // --- Legacy / Compatibility Fields (to prevent build errors in existing components) ---
  currentProject: Project | null;
  openTabs: EditorTab[];
  activeTabId: string | null;
  selectedFileId: string | null;
  chatSessions: ChatSession[];
  activeChatId: string | null;
  isProjectLoading: boolean;
  wsConnectionId: string | null;
  isWaitingAnswer: boolean;
  isGeneratingCode: boolean;
  isGeneratingTests: boolean;
  pendingCodeGeneration: boolean;
  hasReceivedAssistantResponse: boolean;
  invokedTool: string | null;
  isDownloadDialogOpen: boolean;
  downloadProgress: number;
  isTestGenerationForDownloadPending: boolean;
  isDownloadComplete: boolean;

  // --- Actions ---
  setWsConnectionId: (id: string) => void;
  loadProject: (
    id: string,
    preserveTabs?: boolean,
    isPreview?: boolean,
  ) => Promise<void>;
  compileProject: () => Promise<void>;
  downloadProjectZip: (retrieveTests?: boolean) => Promise<void>;
  generateTests: () => Promise<void>;
  runTests: () => Promise<void>;
  openFile: (path: string) => void;
  setActiveFile: (path: string) => void;
  updateFileContent: (path: string, content: string) => void;
  addChatMessage: (message: {
    role: "user" | "assistant" | "system";
    content: string;
    timestamp?: string;
  }) => void;
  sendMessage: (content: string) => Promise<void>;
  appendTerminalOutput: (line: string) => void;
  setTestsSummary: (summary: TestsSummary | null) => void;
  setLoginModalOpen: (open: boolean) => void;

  // --- UI state actions ---
  toggleSidebar: () => void;
  toggleChatPanel: () => void;
  toggleBottomPanel: () => void;
  setBottomPanel: (open: boolean) => void;
  setBottomPanelHeight: (height: number) => void;
  setTerminalActiveTab: (tab: "compile" | "test") => void;
  initialBECalls: () => Promise<void>;
  projects: Project[];

  // CRUD & Management
  setCurrentProject: (project: Project) => void;
  createFile: (
    parentFolderId: string | null,
    fileName: string,
  ) => Promise<void>;
  createFolder: (
    parentFolderId: string | null,
    folderName: string,
  ) => Promise<void>;
  deleteNode: (nodeId: string) => Promise<void>;
  renameNode: (nodeId: string, newName: string) => Promise<void>;
  moveNode: (nodeId: string, targetFolderId: string | null) => Promise<void>;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  selectFile: (fileId: string) => void;
  toggleFolder: (folderId: string) => void;
  createNewChat: () => void;
  setActiveChat: (chatId: string) => void;
  checkHasTests: () => boolean;
  setDownloadDialogOpen: (open: boolean) => void;
  setTestGenerationForDownloadPending: (pending: boolean) => void;
  setDownloadProgress: (progress: number) => void;
  triggerDownloadFlow: () => void;
  downloadLocalZip: () => Promise<void>;
  setDownloadComplete: (complete: boolean) => void;
  upsertFilesToTree: (files: FileNode[]) => void;
  removeFilesFromTree: (paths: string[]) => void;
  reloadProjectFiles: () => Promise<void>;
  clearProjectState: () => void;
  cancelPendingLoad: () => void;
}

const useSherpaStudioStoreBase = create<ProjectStore>((set, get) => ({
  // --- Initial State ---
  projectId: null,
  userId: null,
  fileTree: null,
  expandedFolderKeys: new Set<string>(),
  openFiles: [],
  activeFile: null,
  fileContents: {},
  chatMessages: [],
  terminalOutput: [],
  testsSummary: null,
  loading: false,
  error: null,
  readOnly: false,
  isLoginModalOpen: false,
  projects: [],

  isSidebarCollapsed: false,
  isChatPanelOpen: true,
  isBottomPanelOpen: false,
  bottomPanelHeight: 200,
  terminalActiveTab: "compile",

  currentProject: null,
  openTabs: [],
  activeTabId: null,
  selectedFileId: null,
  chatSessions: [],
  activeChatId: null,
  isProjectLoading: false,
  wsConnectionId: null,
  isWaitingAnswer: false,
  isGeneratingCode: false,
  isGeneratingTests: false,
  pendingCodeGeneration: false,
  hasReceivedAssistantResponse: false,
  invokedTool: null,
  isDownloadDialogOpen: false,
  downloadProgress: 0,
  isTestGenerationForDownloadPending: false,
  isDownloadComplete: false,

  // --- Actions ---
  setWsConnectionId: (id: string) => set({ wsConnectionId: id }),

  loadProject: async (
    id: string,
    preserveTabs: boolean = false,
    isPreview: boolean = false,
  ) => {
    // Increment the global call counter and capture this call's ID.
    // Any state update that runs after a newer call has started will be discarded.
    const callId = ++_loadCallId;

    // Clear results from other stores only when doing a full project switch (not a silent reload)
    const isProjectSwitch = get().projectId !== id;
    if (!preserveTabs || isProjectSwitch) {
      useCompilationStore.getState().actions.clearCompilationResult();
      useUnitTestStore.getState().actions.clearUnitTestResult();
      useCodeGeneratorStore.getState().actions.clearGenerations();
      useDownloadStore.getState().actions.resetDownloadState();
    }

    // Guard: abort if superseded by a newer call
    if (callId !== _loadCallId) return;

    set({
      loading: true,
      error: null,
      projectId: id,
      isProjectLoading: true,
      terminalOutput: [], // Also clear terminal output
      testsSummary: null, // Clear tests summary
    });
    const { wsConnectionId } = get();

    try {
      // Get user ID
      let userId: string | null = null;
      try {
        userId = await getUserTokenFromJWT();
      } catch (e) {
        console.warn("User not authenticated, continuing as guest");
      }

      // Guard: abort if superseded by a newer call
      if (callId !== _loadCallId) return;

      set({ userId, readOnly: !userId });

      // Persist last project ID (skip for previews)
      // Persist last project ID (skip for previews) - handled after loading name/tree

      let tree: FileNode[] = [];
      let contents: Record<string, string> = {};
      let projectName: string = isPreview ? `Template ${id}` : `Project ${id}`;

      let projectData: any;
      // Real BE call
      try {
        if (isPreview) {
          projectData = await getTemplatePreview(id);
        } else {
          projectData = await getProject(id, userId, wsConnectionId || "");
        }

        // Guard: abort if superseded by a newer call
        if (callId !== _loadCallId) return;

        tree = projectData.files || [];
        contents = extractContents(tree);
        projectName = projectData.name || projectName;
      } catch (beErr) {
        console.error("BE project fetch failed:", beErr);
        if (id === "1" && !isPreview) {
          // Hardcoded backup for demo/dev only
          tree = [
            {
              id: "contracts",
              name: "contracts",
              path: "/contracts",
              type: "folder",
              isOpen: true,
              children: [
                {
                  id: "/contracts/1_Storage.sol",
                  name: "1_Storage.sol",
                  path: "/contracts/1_Storage.sol",
                  type: "file",
                  extension: "sol",
                },
                {
                  id: "/contracts/2_Owner.sol",
                  name: "2_Owner.sol",
                  path: "/contracts/2_Owner.sol",
                  type: "file",
                  extension: "sol",
                },
                {
                  id: "/contracts/3_Ballot.sol",
                  name: "3_Ballot.sol",
                  path: "/contracts/3_Ballot.sol",
                  type: "file",
                  extension: "sol",
                },
              ],
            },
            {
              id: "scripts",
              name: "scripts",
              path: "/scripts",
              type: "folder",
              isOpen: true,
              children: [
                {
                  id: "/scripts/deploy.ts",
                  name: "deploy.ts",
                  path: "/scripts/deploy.ts",
                  type: "file",
                  extension: "ts",
                },
                {
                  id: "/scripts/interact.ts",
                  name: "interact.ts",
                  path: "/scripts/interact.ts",
                  type: "file",
                  extension: "ts",
                },
              ],
            },
            {
              id: "tests",
              name: "tests",
              path: "/tests",
              type: "folder",
              isOpen: true,
              children: [
                {
                  id: "/tests/Storage_test.sol",
                  name: "Storage_test.sol",
                  path: "/tests/Storage_test.sol",
                  type: "file",
                  extension: "sol",
                },
              ],
            },
            {
              id: "/README.md",
              name: "README.md",
              path: "/README.md",
              type: "file",
              extension: "md",
            },
          ];

          contents = {
            "/contracts/1_Storage.sol": `// SPDX-License-Identifier: MIT\npragma solidity >=0.8.2 <0.9.0;\n\n/**\n * @title Storage\n * @dev Store & retrieve value in a variable\n */\ncontract Storage {\n\n    uint256 number;\n\n    /**\n     * @dev Store value in variable\n     * @param num value to store\n     */\n    function store(uint256 num) public {\n        number = num;\n    }\n\n    /**\n     * @dev Return value \n     * @return value of 'number'\n     */\n    function retrieve() public view returns (uint256){\n        return number;\n    }\n}`,
            "/contracts/2_Owner.sol": `// SPDX-License-Identifier: MIT\npragma solidity >=0.8.2 <0.9.0;\n\n/**\n * @title Owner\n * @dev Set & change owner\n */\ncontract Owner {\n\n    address private owner;\n    \n    // event for EVM logging\n    event OwnerSet(address indexed oldOwner, address indexed newOwner);\n    \n    // modifier to check if caller is owner\n    modifier isOwner() {\n        require(msg.sender == owner, "Caller is not owner");\n        _;\n    }\n    \n    /**\n     * @dev Set contract deployer as owner\n     */\n    constructor() {\n        owner = msg.sender;\n        emit OwnerSet(address(0), owner);\n    }\n\n    /**\n     * @dev Change owner\n     * @param newOwner address of new owner\n     */\n    function changeOwner(address newOwner) public isOwner {\n        emit OwnerSet(owner, newOwner);\n        owner = newOwner;\n    }\n\n    /**\n     * @dev Return owner address \n     * @return address of owner\n     */\n    function getOwner() external view returns (address) {\n        return owner;\n    }\n}`,
            "/contracts/3_Ballot.sol": `// SPDX-License-Identifier: MIT\npragma solidity >=0.7.0 <0.9.0;\n\ncontract Ballot {\n    struct Voter {\n        uint weight;\n        bool voted;\n        address delegate;\n        uint vote;\n    }\n\n    struct Proposal {\n        bytes32 name;\n        uint voteCount;\n    }\n\n    address public chairperson;\n    mapping(address => Voter) public voters;\n    Proposal[] public proposals;\n\n    constructor(bytes32[] memory proposalNames) {\n        chairperson = msg.sender;\n        voters[chairperson].weight = 1;\n\n        for (uint i = 0; i < proposalNames.length; i++) {\n            proposals.push(Proposal({\n                name: proposalNames[i],\n                voteCount: 0\n            }));\n        }\n    }\n}`,
            "/scripts/deploy.ts": `async function main() {\n  console.log("Deploying contracts...");\n}`,
            "/scripts/interact.ts": `async function main() {\n  console.log("Interacting with contracts...");\n}`,
            "/tests/Storage_test.sol": `// SPDX-License-Identifier: MIT\npragma solidity >=0.8.2 <0.9.0;\nimport "remix_tests.sol";\nimport "../contracts/1_Storage.sol";\n\ncontract StorageTest {\n    Storage storageToTest;\n\n    function beforeAll() public {\n        storageToTest = new Storage();\n    }\n\n    function testStorage() public {\n        storageToTest.store(100);\n        Assert.equal(storageToTest.retrieve(), 100, "Should store 100");\n    }\n}`,
            "/README.md":
              "# Hardcoded Project 1\n\nThis project contains classic Solidity examples like Storge, Owner, and Ballot.",
          };
          projectName = "Hardcoded Example";
        } else {
          throw beErr;
        }
      }

      // Handle preview-specific data (chat history and test results)
      if (isPreview && projectData) {
        if (
          projectData.testResults &&
          Array.isArray(projectData.testResults) &&
          projectData.testResults.length > 0
        ) {
          const rawData = projectData.testResults[0].results;
          if (rawData) {
            const mappedReport = {
              testCode: rawData.testCode || "",
              warnings: rawData.warnings || [],
              recommendations: rawData.recommendations || [],
              executionSummary: rawData.executionSummary || {
                totalTests: rawData.totalTests || 0,
                passedTests: rawData.passedTests || 0,
                failedTests: rawData.failedTests || 0,
                skippedTests: rawData.skippedTests || 0,
                successRate: rawData.successRate || 0,
                executionTime: rawData.executionTime || 0,
                compilationSuccess:
                  rawData.compilationSuccess !== undefined
                    ? rawData.compilationSuccess
                    : true,
                completionAttempts: rawData.completionAttempts || 0,
                coverageCollected: rawData.coverageCollected || false,
                coverage: rawData.coverage,
              },
              finalTestResults: rawData.finalTestResults || [],
            };
            useUnitTestStore.getState().actions.setUnitTestResult(mappedReport);
          }
        }
      }

      const mapMessages = (list: any[]) => {
        return list
          .map((m: any) => {
            let content = "";
            let role = m.role || "assistant";

            if (typeof m.message === "string") {
              try {
                const parsed = JSON.parse(m.message);
                content = parsed.content || m.message;
                if (parsed.role) role = parsed.role;
              } catch (e) {
                content = m.message;
              }
            } else if (m.message && typeof m.message === "object") {
              content = m.message.content || JSON.stringify(m.message);
              if (m.message.role) role = m.message.role;
            } else {
              content = String(m.message || "");
            }

            return {
              id: m.id?.toString() || Math.random().toString(),
              role: role as "user" | "assistant",
              content: content,
              timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
            };
          })
          .reverse();
      };

      const chatStore = useChatMessagesStore.getState();
      const hasWarmChat =
        !isPreview &&
        chatStore.messages.length > 0 &&
        projectIdsMatch(chatStore.historyId, id);

      let historyMessages: any[] = [];

      if (isPreview && projectData?.chatMessageResponseList) {
        historyMessages = mapMessages(projectData.chatMessageResponseList);
      } else if (!hasWarmChat && !isPreview) {
        try {
          const { loadMessagesByHistoryId } =
            await import("../services/chat.services");
          if (callId !== _loadCallId) return;

          const messagesData = await loadMessagesByHistoryId(Number(id), 1);
          if (callId !== _loadCallId) return;

          historyMessages = mapMessages(
            messagesData.chatMessageResponseList || [],
          );
        } catch (chatErr) {
          console.error("Failed to load project chat history:", chatErr);
        }
      }

      if (callId !== _loadCallId) return;

      chatStore.actions.setHistoryId(
        normalizeProjectId(id),
        "sherpaStudioStore.loadProject",
      );

      if (!hasWarmChat && historyMessages.length > 0) {
        const incoming = historyMessages.map((m: any) => ({
          role: (m.role === "user" ? "user" : "assistant") as
            | "user"
            | "assistant",
          content: String(m.content ?? ""),
        }));
        chatStore.actions.setMessages(
          incoming,
          "sherpaStudioStore.loadProject",
        );
      }

      // Determine new open files and active file
      let newOpenFiles = preserveTabs ? get().openFiles : [];
      let newActiveFile = preserveTabs ? get().activeFile : null;
      let newOpenTabs = preserveTabs ? get().openTabs : [];
      let newActiveTabId = preserveTabs ? get().activeTabId : null;

      // If not preserving and no active file, try to open the first contract
      if (!preserveTabs && !newActiveFile) {
        const findFirstContract = (nodes: FileNode[]): string | null => {
          // Sort nodes to prioritize contracts folder
          const sorted = [...nodes].sort((a, b) => {
            if (a.name === "contracts") return -1;
            if (b.name === "contracts") return 1;
            return 0;
          });

          for (const node of sorted) {
            if (
              node.type === "file" &&
              node.path.toLowerCase().endsWith(".sol")
            ) {
              return node.path;
            }
            if (node.children) {
              const found = findFirstContract(node.children);
              if (found) return found;
            }
          }
          return null;
        };

        const firstContractPath = findFirstContract(tree);
        if (firstContractPath) {
          newOpenFiles = [firstContractPath];
          newActiveFile = firstContractPath;
          newActiveTabId = firstContractPath;
          const fileName = firstContractPath.split("/").pop() || "";
          const ext = fileName.split(".").pop() || "";
          newOpenTabs = [
            {
              id: firstContractPath,
              fileId: firstContractPath,
              fileName: fileName,
              filePath: firstContractPath,
              language: getLanguageFromExtension(ext),
              isDirty: false,
            },
          ];
        }
      }

      // If preserving, validate that files still exist
      if (preserveTabs) {
        const existingPaths = Object.keys(contents);
        newOpenFiles = newOpenFiles.filter((p) => existingPaths.includes(p));
        newOpenTabs = newOpenTabs.filter((t) =>
          existingPaths.includes(t.filePath),
        );

        if (newActiveFile && !existingPaths.includes(newActiveFile)) {
          newActiveFile = newOpenFiles.length > 0 ? newOpenFiles[0] : null;
          newActiveTabId = newActiveFile;
        }
      }

      // Final state update
      set({
        loading: false,
        isProjectLoading: false,
        fileTree: tree,
        expandedFolderKeys: isProjectSwitch
          ? collectExpandedKeysFromTree(tree)
          : get().expandedFolderKeys,
        fileContents: contents,
        openFiles: newOpenFiles,
        activeFile: newActiveFile,
        chatMessages: historyMessages,
        currentProject: {
          id: id,
          name: projectName,
          files: tree,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        chatSessions: [
          {
            id: id,
            name: projectName,
            messages: historyMessages,
            createdAt: new Date(),
          },
        ],
        activeChatId: id,
        openTabs: newOpenTabs,
        activeTabId: newActiveTabId,
        selectedFileId: newActiveFile,
      });

      // Persist project name and history
      if (!isPreview) {
        addRecentProject(
          {
            id,
            name: projectName,
            hasContract: checkIfHasContract(tree),
          },
          userId,
        );
      }

      // Try to fetch existing test report if there are test files
      if (!isPreview) {
        const findTestFile = (nodes: FileNode[]): string | null => {
          for (const node of nodes) {
            if (
              node.type === "folder" &&
              (node.name === "tests" || node.name === "test")
            ) {
              const testFile = node.children?.find(
                (child) => child.type === "file" && child.extension === "ts",
              );
              if (testFile) return testFile.name;
            }
            if (node.children) {
              const found = findTestFile(node.children);
              if (found) return found;
            }
          }
          return null;
        };

        const testFileName = findTestFile(tree);
        if (testFileName) {
          try {
            const { getTestReport } =
              await import("../services/unitTest.services");
            if (callId !== _loadCallId) return; // Guard after import

            const report = await getTestReport(Number(id), testFileName);
            if (callId !== _loadCallId) return; // Guard after fetch

            if (report) {
              useUnitTestStore.getState().actions.setUnitTestResult(report);
            }
          } catch (e) {
            // Silently ignore if no report exists
            console.log("No initial test report found for", testFileName);
          }
        }
      }
    } catch (err: any) {
      set({ loading: false, isProjectLoading: false, error: err.message });
      console.error("Failed to load project:", err);
    }
  },

  compileProject: async () => {
    const { projectId, wsConnectionId, readOnly } =
      get();
    if (readOnly) {
      set({ isLoginModalOpen: true });
      return;
    }
    if(!wsConnectionId || !projectId) {
      console.error("Missing required fields for compileProject")
      return;
    }

    const compilationActions = useCompilationStore.getState().actions;
    compilationActions.setIsCompiling(true);
    set({ terminalActiveTab: "compile", isBottomPanelOpen: true });

    try {
      // Keep compile state/result driven by websocket events to avoid
      // transient UI flips caused by mixing HTTP and WS updates.
      await compileSolidity(wsConnectionId, projectId);
    } catch (error: any) {
      compilationActions.setCompilationResult({
        status: "Not compiled",
        errors: error.message || "An unknown error occurred",
      });
      compilationActions.setIsCompiling(false);
    }
  },

  downloadProjectZip: async (retrieveTests: boolean = true) => {
    const { projectId } = get();
    if (!projectId) return;

    try {
      set({ loading: true });
      const base64 = await downloadProject(projectId, retrieveTests, false);
      if (base64) {
        downloadBase64File(base64, "project.zip");
      }
      set({ loading: false });
    } catch (err: any) {
      set({ loading: false, error: err.message });
      console.error("Download failed:", err);
    }
  },

  generateTests: async () => {
    const { projectId, wsConnectionId, activeFile, activeTabId } = get();
    if (!projectId || !wsConnectionId) return;

    const contractPath = resolveActiveContractPath(activeFile, activeTabId);
    const unitTestActions = useUnitTestStore.getState().actions;
    if (!contractPath) {
      unitTestActions.setError(
        "Open a Solidity contract file before generating tests.",
      );
      unitTestActions.setIsGenerating(false);
      unitTestActions.setTestPhase(null);
      return;
    }
    unitTestActions.setTargetFile(contractPath);

    if (useSubscriptionStore.getState().hasExceededTokenLimit()) {
      const tokenUsage = useSubscriptionStore.getState().tokenUsage;
      set({
        loading: false,
        error:
          "Token limit exceeded. Upgrade your plan or wait for the reset date to continue.",
      });
      if (tokenUsage?.resetDate) {
        // Keep the error informative for logs/consumers that surface it.
        console.warn(`Token limit exceeded. Resets on ${tokenUsage.resetDate}`);
      }
      return;
    }

    try {
      set({ loading: true });
      const { generateUnitTests } =
        await import("../services/unitTest.services");
      await generateUnitTests(
        projectId,
        wsConnectionId,
        contractPath,
        false,
        false,
      );
      set({ loading: false });
    } catch (err: any) {
      if (err?.status === 403 && err?.tokenStatus) {
        useSubscriptionStore.getState().updateTokenStatusFromError(err);
      }
      set({ loading: false, error: err.message });
    }
  },

  runTests: async (testPath?: string) => {
    const { projectId, wsConnectionId, readOnly, activeTabId } = get();
    if (readOnly) {
      set({ isLoginModalOpen: true });
      return;
    }

    const targetPath = testPath || activeTabId;

    if (!projectId || !wsConnectionId || !targetPath) {
      console.error("Missing required fields for runTests:", {
        projectId,
        wsConnectionId,
        targetPath,
      });
      return;
    }

    try {
      // Optioanlly set loading state in unitTestStore
      const unitTestActions = useUnitTestStore.getState().actions;
      unitTestActions.setIsGenerating(true); // Using isGenerating as a "loading" state for tests
      unitTestActions.setTestPhase("execution"); // Show execution state immediately in the UI
      set({ terminalActiveTab: "test", isBottomPanelOpen: true });

      const { executeTest } = await import("../services/unitTest.services");
      await executeTest(projectId, targetPath, wsConnectionId);
    } catch (err: any) {
      console.error("Run tests failed:", err);
      const unitTestActions = useUnitTestStore.getState().actions;
      unitTestActions.setError(err.message || "Failed to execute tests");
      unitTestActions.setTestPhase(null);
    }
  },

  openFile: (path: string) => {
    const { openFiles } = get();
    if (!openFiles.includes(path)) {
      set({ openFiles: [...openFiles, path], activeFile: path });
    } else {
      set({ activeFile: path });
    }

    const fileName = path.split("/").pop() || "file";
    const extension = fileName.split(".").pop() || "txt";
    const newTab: EditorTab = {
      id: path,
      fileId: path,
      fileName: fileName,
      filePath: path,
      language: getLanguageFromExtension(extension),
      isDirty: false,
    };

    const { openTabs } = get();
    if (!openTabs.find((t) => t.id === path)) {
      set({
        openTabs: [...openTabs, newTab],
        activeTabId: path,
        selectedFileId: path,
      });
    } else {
      set({ activeTabId: path, selectedFileId: path });
    }
  },

  setActiveFile: (path: string) => {
    set({ activeFile: path, activeTabId: path, selectedFileId: path });
  },

  updateFileContent: (path: string, content: string) => {
    const state = get();
    if (state.fileTree) {
      const targetNode = findNodeByPath(state.fileTree, path);
      if (targetNode?.locked) return;
    }
    const current = state.fileContents[path];

    // Only update if content actually changed to avoid unnecessary dirty markers
    if (current === content) return;

    set((state) => ({
      fileContents: {
        ...state.fileContents,
        [path]: content,
      },
      openTabs: state.openTabs.map((tab) =>
        tab.filePath === path ? { ...tab, isDirty: true } : tab,
      ),
    }));

    // Auto-save via handleFiles - Use per-path debouncing to avoid cancelling saves when switching files
    const { projectId, readOnly } = get();
    if (projectId && !shouldSkipHandleFiles(readOnly)) {
      if (!(window as any)._autoSaveTimeouts)
        (window as any)._autoSaveTimeouts = {};

      if ((window as any)._autoSaveTimeouts[path]) {
        clearTimeout((window as any)._autoSaveTimeouts[path]);
      }

      (window as any)._autoSaveTimeouts[path] = setTimeout(async () => {
        const result = await handleFiles({
          projectId,
          operation: "update",
          files: [{ path, newContent: content }],
        });

        if (isHandleFilesSuccess(result)) {
          set((state) => ({
            openTabs: state.openTabs.map((tab) =>
              tab.filePath === path ? { ...tab, isDirty: false } : tab,
            ),
          }));
        } else {
          const message = useNotificationStore
            .getState()
            .actions.notifyError("studio_autosave_failed");
          set({ error: message });
        }
        delete (window as any)._autoSaveTimeouts[path];
      }, 1000); // Debounce auto-save by 1 second
    }
  },

  upsertFilesToTree: (files: FileNode[]) => {
    if (!files?.length) return;

    set((state) => {
      const baseTree = state.fileTree ? cloneNodes(state.fileTree) : [];
      const uniqueIncomingFiles = dedupeNodesByPath(files);
      const nextTree = uniqueIncomingFiles.reduce(
        (acc, file) => upsertSingleNode(acc, file),
        baseTree,
      );
      const incomingContents = buildFileContentsMap(uniqueIncomingFiles);

      return {
        fileTree: nextTree,
        fileContents: {
          ...state.fileContents,
          ...incomingContents,
        },
        currentProject: state.currentProject
          ? { ...state.currentProject, files: nextTree }
          : state.currentProject,
      };
    });
  },

  removeFilesFromTree: (paths: string[]) => {
    if (!paths?.length) return;

    const normalizedDeletedPaths = Array.from(
      new Set(
        paths
          .map((path) => normalizeNodePath(path || ""))
          .filter((path) => path.length > 0),
      ),
    );
    if (normalizedDeletedPaths.length === 0) return;

    const isPathDeleted = (path: string | null | undefined): boolean => {
      if (!path) return false;
      const normalized = normalizeNodePath(path);
      if (!normalized) return false;
      return normalizedDeletedPaths.some(
        (deleted) =>
          normalized === deleted || normalized.startsWith(`${deleted}/`),
      );
    };

    const removeFromTree = (nodes: FileNode[]): FileNode[] =>
      nodes
        .filter((node) => !isPathDeleted(node.path))
        .map((node) =>
          node.children
            ? { ...node, children: removeFromTree(node.children) }
            : node,
        );

    set((state) => {
      const nextTree = state.fileTree ? removeFromTree(state.fileTree) : state.fileTree;

      const nextFileContents: Record<string, string> = {};
      Object.entries(state.fileContents).forEach(([key, value]) => {
        if (!isPathDeleted(key)) nextFileContents[key] = value;
      });

      const nextOpenFiles = state.openFiles.filter(
        (path) => !isPathDeleted(path),
      );
      const nextActiveFile = isPathDeleted(state.activeFile)
        ? null
        : state.activeFile;

      const nextOpenTabs = state.openTabs.filter(
        (tab) => !isPathDeleted(tab.filePath),
      );
      const nextActiveTabId = isPathDeleted(state.activeTabId)
        ? null
        : state.activeTabId;
      const nextSelectedFileId = isPathDeleted(state.selectedFileId)
        ? null
        : state.selectedFileId;

      const nextExpandedKeys = normalizedDeletedPaths.reduce(
        (acc, deleted) => pruneExpandedFolderKeys(acc, deleted),
        state.expandedFolderKeys,
      );

      return {
        fileTree: nextTree,
        fileContents: nextFileContents,
        openFiles: nextOpenFiles,
        activeFile: nextActiveFile,
        openTabs: nextOpenTabs,
        activeTabId: nextActiveTabId,
        selectedFileId: nextSelectedFileId,
        expandedFolderKeys: nextExpandedKeys,
        currentProject: state.currentProject
          ? { ...state.currentProject, files: nextTree || [] }
          : state.currentProject,
      };
    });
  },

  addChatMessage: (message) => {
    let sanitizedContent = message.content;
    if (typeof sanitizedContent !== "string") {
      sanitizedContent =
        (sanitizedContent as any)?.content ||
        JSON.stringify(sanitizedContent || "");
    }

    const role =
      message.role === "user" || message.role === "assistant"
        ? message.role
        : "assistant";
    const chatLine = { content: sanitizedContent, role } as const;

    useChatMessagesStore
      .getState()
      .actions.addMessage(chatLine, "sherpaStudioStore.addChatMessage");

    const newMessage = {
      ...message,
      content: sanitizedContent,
      timestamp: message.timestamp || new Date().toISOString(),
    };

    set((state) => ({
      chatMessages: [...state.chatMessages, newMessage],
      chatSessions: state.chatSessions.map((s) => {
        if (s.id === state.activeChatId) {
          return {
            ...s,
            messages: [
              ...s.messages,
              {
                ...newMessage,
                id: Date.now().toString(),
                timestamp: new Date(newMessage.timestamp),
              },
            ],
          };
        }
        return s;
      }),
    }));
  },

  sendMessage: async (content: string) => {
    const { projectId, wsConnectionId, isWaitingAnswer } = get();
    const chatStore = useChatMessagesStore.getState();
    if (!content.trim() || isWaitingAnswer || chatStore.isWaitingAnswer || !projectId) {
      return;
    }

    const chatActions = chatStore.actions;
    chatActions.setHasReceivedAssistantResponse(false);
    chatActions.setPendingCodeGeneration(false);
    chatActions.setIsWaitingAnswer(true, "sherpaStudioStore.sendMessage");

    set({
      hasReceivedAssistantResponse: false,
      pendingCodeGeneration: false,
      isWaitingAnswer: true,
    });

    const userMsg = { role: "user" as const, content };
    chatActions.addMessage(userMsg, "sherpaStudioStore.sendMessage");
    set((state) => ({
      chatMessages: [
        ...state.chatMessages,
        {
          ...userMsg,
          timestamp: new Date().toISOString(),
        },
      ],
    }));

    const chatId =
      normalizeProjectId(chatStore.historyId) ||
      normalizeProjectId(projectId);

    try {
      const { callGptMemory } = await import("../services/chat.services");
      const response = await callGptMemory(
        useChatMessagesStore.getState().messages,
        content,
        chatId,
        wsConnectionId || "",
      );
      const resolvedChatId = extractChatIdFromResponse(response);
      if (resolvedChatId) {
        chatActions.setHistoryId(resolvedChatId, "sherpaStudioStore.sendMessage");
      }
    } catch (error: any) {
      console.error("Error sending message:", error);
      chatActions.setIsWaitingAnswer(false, "sherpaStudioStore.sendMessage error");
      set({ isWaitingAnswer: false });
    }
  },

  appendTerminalOutput: (line: string) => {
    set((state) => ({
      terminalOutput: [...state.terminalOutput, line],
    }));
  },

  setTestsSummary: (summary) => set({ testsSummary: summary }),
  setLoginModalOpen: (open) => set({ isLoginModalOpen: open }),

  toggleSidebar: () =>
    set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
  toggleChatPanel: () =>
    set((state) => ({ isChatPanelOpen: !state.isChatPanelOpen })),
  toggleBottomPanel: () =>
    set((state) => ({ isBottomPanelOpen: !state.isBottomPanelOpen })),
  setBottomPanel: (open) => set({ isBottomPanelOpen: open }),
  setBottomPanelHeight: (height) => set({ bottomPanelHeight: height }),
  setTerminalActiveTab: (tab) => set({ terminalActiveTab: tab }),

  initialBECalls: async () => {
    try {
      const { loadHistory } = await import("../services/chat.services");
      const data = await loadHistory(1);
      const projects = (data.chatHistoryResponseList || []).map((h: any) => ({
        id: h.historyId.toString(),
        name: h.title || h.summary || `Project ${h.historyId}`,
        files: [],
        updatedAt: new Date(h.lastUpdateTmst || h.createdAt),
        createdAt: new Date(h.createdAt),
      }));
      set({ projects });
    } catch (err) {
      console.error("Failed to load project history:", err);
    }
  },

  setCurrentProject: (project) => {
    addRecentProject({
      id: project.id,
      name: project.name,
      hasContract: checkIfHasContract(project.files),
    });
    set({
      currentProject: project,
      projectId: project.id,
      fileTree: project.files,
    });
  },

  createFile: async (parentFolderId, fileName) => {
    const { fileTree, currentProject } = get();
    if (!fileTree) return;

    const findNodePathById = (nodes: FileNode[], id: string): string | null => {
      for (const node of nodes) {
        if (node.id === id) return node.path;
        if (node.children) {
          const found = findNodePathById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const parentPathFromTree = parentFolderId
      ? findNodePathById(fileTree, parentFolderId)
      : "";
    const cleanParent = (parentPathFromTree || "").replace(/^\//, "");
    const cleanFileName = fileName.replace(/^\//, "");
    const newPath = cleanParent
      ? `${cleanParent}/${cleanFileName}`.replace(/\/+/g, "/")
      : cleanFileName;

    const extension = cleanFileName.split(".").pop() || "";
    const newFile: FileNode = {
      id: `file-${Date.now()}`,
      name: cleanFileName,
      type: "file",
      path: newPath,
      extension,
      content: "",
    };

    const { projectId, readOnly } = get();
    if (projectId && !shouldSkipHandleFiles(readOnly)) {
      const result = await handleFiles({
        projectId,
        operation: "create",
        files: [{ type: "file", path: newFile.path }],
      });
      if (!isHandleFilesSuccess(result)) {
        const message = useNotificationStore
          .getState()
          .actions.notifyError("studio_create_file_failed");
        set({ error: message });
        return;
      }
    }
    get().upsertFilesToTree([newFile]);
  },

  createFolder: async (parentFolderId, folderName) => {
    const { fileTree, currentProject } = get();
    if (!fileTree) return;

    const findNodePathById = (nodes: FileNode[], id: string): string | null => {
      for (const node of nodes) {
        if (node.id === id) return node.path;
        if (node.children) {
          const found = findNodePathById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const parentPathFromTree = parentFolderId
      ? findNodePathById(fileTree, parentFolderId)
      : "";
    const cleanParent = (parentPathFromTree || "").replace(/^\//, "");
    const cleanFolderName = folderName.replace(/^\//, "");
    const newPath = cleanParent
      ? `${cleanParent}/${cleanFolderName}`.replace(/\/+/g, "/")
      : cleanFolderName;

    const newFolder: FileNode = {
      id: `folder-${Date.now()}`,
      name: cleanFolderName,
      type: "folder",
      path: newPath,
      children: [],
    };

    const { projectId, readOnly } = get();
    if (projectId && !shouldSkipHandleFiles(readOnly)) {
      const result = await handleFiles({
        projectId,
        operation: "create",
        files: [{ type: "folder", path: newFolder.path }],
      });
      if (!isHandleFilesSuccess(result)) {
        const message = useNotificationStore
          .getState()
          .actions.notifyError("studio_create_folder_failed");
        set({ error: message });
        return;
      }
    }
    get().upsertFilesToTree([newFolder]);
  },

  deleteNode: async (nodeId) => {
    const { fileTree, currentProject } = get();
    if (!fileTree) return;
    const nodeToDelete = findNodeById(fileTree, nodeId);
    if (nodeToDelete?.locked) return;

    const removeNode = (nodes: FileNode[]): FileNode[] => {
      return nodes
        .filter((node) => node.id !== nodeId)
        .map((node) => {
          if (node.children)
            return { ...node, children: removeNode(node.children) };
          return node;
        });
    };

    const findPath = (nodes: FileNode[], id: string): string | null => {
      for (const node of nodes) {
        if (node.id === id) return node.path;
        if (node.children) {
          const found = findPath(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };
    const pathToDelete = findPath(fileTree, nodeId);

    const { projectId, readOnly } = get();
    if (projectId && !shouldSkipHandleFiles(readOnly)) {
      const result = await handleFiles({
        projectId,
        operation: "delete",
        files: [{ path: pathToDelete || undefined, nodeId: nodeId }],
      });
      if (!isHandleFilesSuccess(result)) {
        const message = useNotificationStore
          .getState()
          .actions.notifyError("studio_delete_node_failed");
        set({ error: message });
        return;
      }
    }

    const newTree = removeNode(fileTree);
    const deletedPrefix = pathToDelete
      ? normalizeNodePath(pathToDelete)
      : normalizeNodePath(nodeId);
    set((state) => ({
      fileTree: newTree,
      expandedFolderKeys: pruneExpandedFolderKeys(
        state.expandedFolderKeys,
        deletedPrefix,
      ),
      currentProject: state.currentProject
        ? { ...state.currentProject, files: newTree }
        : state.currentProject,
    }));
  },

  renameNode: async (nodeId, newName) => {
    const { fileTree, currentProject } = get();
    if (!fileTree) return;
    const nodeToRename = findNodeById(fileTree, nodeId);
    if (!nodeToRename || nodeToRename.locked) return;
    const oldPath = normalizeNodePath(nodeToRename.path);
    const cleanNewName = newName.replace(/^\//, "");
    const parentPath = oldPath.split("/").slice(0, -1).join("/");
    const newPath = parentPath ? `${parentPath}/${cleanNewName}` : cleanNewName;

    const { projectId, readOnly } = get();
    if (projectId && !shouldSkipHandleFiles(readOnly)) {
      const result = await handleFiles({
        projectId,
        operation: "update",
        files: [
          {
            path: oldPath || undefined,
            newName,
          },
        ],
      } as any);
      if (!isHandleFilesSuccess(result)) {
        const message = useNotificationStore
          .getState()
          .actions.notifyError("studio_rename_node_failed");
        set({ error: message });
        return;
      }
    }

    const updatePathsRecursively = (
      node: FileNode,
      parent: string,
    ): FileNode => {
      const cleanParent = parent.replace(/^\//, "").replace(/\/+$/, "");
      const cleanNodeName = node.name.replace(/^\//, "");
      const nodePath = cleanParent
        ? `${cleanParent}/${cleanNodeName}`.replace(/\/+/g, "/")
        : cleanNodeName;
      return {
        ...node,
        path: nodePath,
        children: node.children
          ? node.children.map((child) =>
              updatePathsRecursively(child, nodePath),
            )
          : undefined,
      };
    };

    const renameInNodes = (nodes: FileNode[]): FileNode[] =>
      nodes.map((node) => {
        if (node.id === nodeId) {
          const renamedNode = { ...node, name: cleanNewName };
          return updatePathsRecursively(renamedNode, parentPath);
        }
        if (node.children)
          return { ...node, children: renameInNodes(node.children) };
        return node;
      });

    const newTree = renameInNodes(fileTree);
    set((state) => ({
      fileTree: newTree,
      expandedFolderKeys: remapExpandedFolderKeys(
        state.expandedFolderKeys,
        oldPath,
        newPath,
      ),
      currentProject: currentProject
        ? { ...currentProject, files: newTree }
        : state.currentProject,
      fileContents: remapPathKeys(state.fileContents, oldPath, newPath),
      openFiles: state.openFiles.map(
        (path) => mapPathPrefix(path, oldPath, newPath) || path,
      ),
      activeFile: mapPathPrefix(state.activeFile, oldPath, newPath),
      openTabs: state.openTabs.map((tab) => ({
        ...tab,
        id: mapPathPrefix(tab.id, oldPath, newPath) || tab.id,
        fileId: mapPathPrefix(tab.fileId, oldPath, newPath) || tab.fileId,
        filePath: mapPathPrefix(tab.filePath, oldPath, newPath) || tab.filePath,
      })),
      activeTabId: mapPathPrefix(state.activeTabId, oldPath, newPath),
      selectedFileId: mapPathPrefix(state.selectedFileId, oldPath, newPath),
    }));
  },

  moveNode: async (nodeId: string, targetFolderId: string | null) => {
    const { fileTree, currentProject } = get();
    if (!fileTree) return;
    const nodeToMove = findNodeById(fileTree, nodeId);
    if (nodeToMove?.locked) return;

    let extractedNode: FileNode | null = null;
    const findAndRemove = (nodes: FileNode[]): FileNode[] => {
      return nodes
        .filter((node) => {
          if (node.id === nodeId) {
            extractedNode = node;
            return false;
          }
          return true;
        })
        .map((node) => {
          if (node.children)
            return { ...node, children: findAndRemove(node.children) };
          return node;
        });
    };

    const treeWithoutNode = findAndRemove(fileTree);
    if (!extractedNode) return;

    const getPath = (nodes: FileNode[], id: string): string | null => {
      for (const node of nodes) {
        if (node.id === id) return node.path;
        if (node.children) {
          const found = getPath(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const targetBasePath = targetFolderId
      ? getPath(treeWithoutNode, targetFolderId) || ""
      : "";

    // Updates path recursively for moved node and its children
    const updatePathsRecursively = (
      node: FileNode,
      parentPath: string,
    ): FileNode => {
      const cleanParent = parentPath.replace(/^\//, "").replace(/\/+$/, "");
      const cleanNodeName = node.name.replace(/^\//, "");
      const newPath = cleanParent
        ? `${cleanParent}/${cleanNodeName}`.replace(/\/+/g, "/")
        : cleanNodeName;
      return {
        ...node,
        name: cleanNodeName,
        path: newPath,
        children: node.children
          ? node.children.map((child) => updatePathsRecursively(child, newPath))
          : undefined,
      };
    };

    const nodeToMoveObj = extractedNode as unknown as FileNode;
    const oldPath = nodeToMoveObj.path;
    const updatedNodeToMove = updatePathsRecursively(
      nodeToMoveObj,
      targetBasePath,
    );

    const addToTarget = (nodes: FileNode[]): FileNode[] => {
      if (!targetFolderId) return [...nodes, updatedNodeToMove];
      return nodes.map((node) => {
        if (node.id === targetFolderId && node.type === "folder") {
          return {
            ...node,
            children: [...(node.children || []), updatedNodeToMove],
          };
        }
        if (node.children)
          return { ...node, children: addToTarget(node.children) };
        return node;
      });
    };

    const { projectId, readOnly } = get();
    if (projectId && !shouldSkipHandleFiles(readOnly)) {
      const result = await handleFiles({
        projectId,
        operation: "update",
        files: [
          { path: oldPath || undefined, newPath: updatedNodeToMove.path },
        ],
      } as any);
      if (!isHandleFilesSuccess(result)) {
        const message = useNotificationStore
          .getState()
          .actions.notifyError("studio_move_node_failed");
        set({ error: message });
        return;
      }
    }

    const newTree = addToTarget(treeWithoutNode);
    const oldPrefix = normalizeNodePath(oldPath);
    const newPrefix = normalizeNodePath(updatedNodeToMove.path);
    const targetFolderKey = targetFolderId
      ? normalizeNodePath(targetBasePath)
      : null;
    set((state) => {
      let expandedFolderKeys = remapExpandedFolderKeys(
        state.expandedFolderKeys,
        oldPrefix,
        newPrefix,
      );
      if (targetFolderKey) {
        expandedFolderKeys = new Set(expandedFolderKeys);
        expandedFolderKeys.add(targetFolderKey);
      }
      return {
        fileTree: newTree,
        expandedFolderKeys,
        currentProject: currentProject
          ? { ...currentProject, files: newTree }
          : state.currentProject,
        fileContents: remapPathKeys(state.fileContents, oldPrefix, newPrefix),
        openFiles: state.openFiles.map(
          (path) => mapPathPrefix(path, oldPrefix, newPrefix) || path,
        ),
        activeFile: mapPathPrefix(state.activeFile, oldPrefix, newPrefix),
        openTabs: state.openTabs.map((tab) => ({
          ...tab,
          id: mapPathPrefix(tab.id, oldPrefix, newPrefix) || tab.id,
          fileId: mapPathPrefix(tab.fileId, oldPrefix, newPrefix) || tab.fileId,
          filePath:
            mapPathPrefix(tab.filePath, oldPrefix, newPrefix) || tab.filePath,
        })),
        activeTabId: mapPathPrefix(state.activeTabId, oldPrefix, newPrefix),
        selectedFileId: mapPathPrefix(
          state.selectedFileId,
          oldPrefix,
          newPrefix,
        ),
      };
    });
  },

  closeTab: (tabId: string) => {
    const { openFiles, activeFile } = get();
    const newOpenFiles = openFiles.filter((p) => p !== tabId);
    let newActiveFile = activeFile;
    if (activeFile === tabId) {
      newActiveFile =
        newOpenFiles.length > 0 ? newOpenFiles[newOpenFiles.length - 1] : null;
    }
    set({ openFiles: newOpenFiles, activeFile: newActiveFile });

    const { openTabs } = get();
    const newTabs = openTabs.filter((t) => t.id !== tabId);
    set({ openTabs: newTabs, activeTabId: newActiveFile });
  },

  setActiveTab: (tabId: string) => get().setActiveFile(tabId),
  selectFile: (fileId: string) => set({ selectedFileId: fileId }),

  toggleFolder: (folderId: string) => {
    const { fileTree } = get();
    if (!fileTree) return;
    const node = findNodeById(fileTree, folderId);
    if (!node || node.type !== "folder") return;

    const pathKey = folderExpandKey(node);
    const idKey = normalizeNodePath(node.id);

    set((state) => {
      const next = new Set(state.expandedFolderKeys);
      const isExpanded =
        next.has(pathKey) || (idKey !== pathKey && next.has(idKey));
      if (isExpanded) {
        next.delete(pathKey);
        next.delete(idKey);
      } else {
        next.add(pathKey);
      }
      return { expandedFolderKeys: next };
    });
  },

  createNewChat: () => {
    const newChat: ChatSession = {
      id: `chat-${Date.now()}`,
      name: "New Chat",
      messages: [],
      createdAt: new Date(),
    };
    set((state) => ({
      chatSessions: [...state.chatSessions, newChat],
      activeChatId: newChat.id,
    }));
  },

  setActiveChat: (chatId: string) => set({ activeChatId: chatId }),

  checkHasTests: () => {
    const { fileTree, openTabs, fileContents } = get();
    if (!fileTree) return false;

    if (openTabs.some((tab) => tab.isDirty)) return false;

    const isContentDivergent = (nodes: FileNode[]): boolean => {
      for (const node of nodes) {
        if (node.type === "file") {
          const mapContent = fileContents[node.path] ?? "";
          const treeContent = node.content ?? "";
          if (mapContent !== treeContent) return true;
        }
        if (node.children && isContentDivergent(node.children)) return true;
      }
      return false;
    };

    if (isContentDivergent(fileTree)) return false;

    const lookForTests = (nodes: FileNode[]): boolean => {
      for (const node of nodes) {
        if (
          node.type === "folder" &&
          (node.name === "tests" || node.name === "test")
        ) {
          if (node.children && node.children.length > 0) return true;
        }
        if (node.children && lookForTests(node.children)) return true;
      }
      return false;
    };

    return lookForTests(fileTree);
  },

  setDownloadDialogOpen: (open: boolean) =>
    set((state) => ({
      isDownloadDialogOpen: open,
      isDownloadComplete: open ? state.isDownloadComplete : false,
      downloadProgress: open ? state.downloadProgress : 0,
    })),
  setTestGenerationForDownloadPending: (pending: boolean) =>
    set({ isTestGenerationForDownloadPending: pending }),
  setDownloadProgress: (progress: number) =>
    set({ downloadProgress: progress }),
  setDownloadComplete: (complete: boolean) =>
    set({ isDownloadComplete: complete }),

  downloadLocalZip: async () => {
    const { currentProject, fileContents } = get();
    if (!currentProject) return;

    set({ loading: true });
    try {
      const zip = new JSZip();
      const collectFiles = (nodes: FileNode[]) => {
        nodes.forEach((node) => {
          if (node.type === "file") {
            const content = fileContents[node.path] || node.content || "";
            const cleanPath = node.path.startsWith("/")
              ? node.path.substring(1)
              : node.path;
            zip.file(cleanPath, content);
          } else if (node.children) {
            collectFiles(node.children);
          }
        });
      };
      collectFiles(currentProject.files);
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${currentProject.name || "project"}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      set({ loading: false });
    } catch (error) {
      console.error("Local download failed:", error);
      set({ loading: false, error: "Failed to create zip" });
    }
  },

  triggerDownloadFlow: () => {
    const { checkHasTests } = get();
    if (checkHasTests()) {
      set({
        isDownloadDialogOpen: true,
        isTestGenerationForDownloadPending: false,
        isDownloadComplete: false,
        downloadProgress: 0,
      });
    } else {
      set({
        isDownloadDialogOpen: true,
        isTestGenerationForDownloadPending: true,
        isDownloadComplete: false,
        downloadProgress: 0,
      });
    }
  },

  reloadProjectFiles: async () => {
    const { projectId, wsConnectionId, userId } = get();
    if (!projectId) return;

    try {
      const projectData = await getProject(
        projectId,
        userId,
        wsConnectionId || "",
      );
      const incomingTree = projectData.files || [];
      const contents = extractContents(incomingTree);

      set((state) => ({
        fileTree: incomingTree,
        fileContents: {
          ...state.fileContents,
          ...contents,
        },
        currentProject: state.currentProject
          ? { ...state.currentProject, files: incomingTree }
          : state.currentProject,
      }));
    } catch (err) {
      console.error("Failed to reload project files:", err);
    }
  },

  clearProjectState: () => {
    set({
      projectId: null,
      fileTree: null,
      expandedFolderKeys: new Set<string>(),
      openFiles: [],
      activeFile: null,
      fileContents: {},
      chatMessages: [],
      terminalOutput: [],
      testsSummary: null,
      currentProject: null,
      openTabs: [],
      activeTabId: null,
      selectedFileId: null,
      chatSessions: [],
      activeChatId: null,
      isBottomPanelOpen: false,
      isDownloadComplete: false,
      downloadProgress: 0,
      isTestGenerationForDownloadPending: false,
      invokedTool: null,
      pendingCodeGeneration: false,
      hasReceivedAssistantResponse: false,
      isWaitingAnswer: false,
      isGeneratingCode: false,
      isGeneratingTests: false,
      userId: null,
      readOnly: false,
      isProjectLoading: false,
    });
  },

  cancelPendingLoad: () => {
    // Incrementing the call ID immediately invalidates any ongoing loadProject call
    _loadCallId++;
    set({ isProjectLoading: false, loading: false });
  },
}));

import { createSelectors } from "./createSelectorsFunction";
export const useSherpaStudioStore = createSelectors(useSherpaStudioStoreBase);

export const getActiveContractPath = (): string | null => {
  const { activeFile, activeTabId } = useSherpaStudioStoreBase.getState();
  return resolveActiveContractPath(activeFile, activeTabId);
};
